import React, { useState, useEffect } from 'react';
import { subscribeDepartments, saveDepartmentNames, DEFAULT_DEPARTMENT_NAMES } from '../services/departmentsDb.js';
import { Plus, Trash2 } from 'lucide-react';

export default function DepartmentSettings({ showToast }) {
  const [list, setList] = useState([...DEFAULT_DEPARTMENT_NAMES]);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsub = subscribeDepartments((names) => {
      setList([...names]);
    });
    return () => unsub();
  }, []);

  const persist = async (next) => {
    setSaving(true);
    try {
      await saveDepartmentNames(next);
      showToast('Departments saved', 'ok');
    } catch (e) {
      console.error(e);
      showToast(e.message || 'Could not save departments', 'err');
    } finally {
      setSaving(false);
    }
  };

  const add = async () => {
    const t = newName.trim();
    if (!t) return;
    if (list.some((x) => x.toLowerCase() === t.toLowerCase())) {
      showToast('Department already exists', 'err');
      return;
    }
    const next = [...list, t];
    setList(next);
    setNewName('');
    await persist(next);
  };

  const remove = async (name) => {
    if (list.length <= 1) {
      showToast('At least one department must remain', 'err');
      return;
    }
    if (!window.confirm(`Remove "${name}"? Existing users keep this value until you edit them.`)) return;
    const next = list.filter((x) => x !== name);
    setList(next);
    await persist(next);
  };

  return (
    <div className="pg">
      <div className="ph" style={{ marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '700' }}>Departments</h1>
          <p style={{ color: 'var(--text2)', fontSize: '13px', marginTop: '4px' }}>
            Add or remove departments. These options appear in login requests, approvals, and employee profiles.
          </p>
        </div>
      </div>

      <div className="card" style={{ padding: '20px', maxWidth: '560px' }}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
            placeholder="New department name"
            disabled={saving}
            style={{
              flex: '1 1 200px',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              fontSize: '14px',
            }}
          />
          <button type="button" className="btn btn-p" onClick={add} disabled={saving || !newName.trim()} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <Plus size={18} /> Add
          </button>
        </div>

        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {list.map((name) => (
            <li
              key={name}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 14px',
                borderBottom: '1px solid var(--border)',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              <span>{name}</span>
              <button
                type="button"
                className="btn"
                onClick={() => remove(name)}
                disabled={saving || list.length <= 1}
                style={{
                  padding: '6px 10px',
                  color: 'var(--red, #c0392b)',
                  border: '1px solid var(--border)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
                title="Remove department"
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
